import { useState } from "react"
import { X } from "lucide-react"

import { AdvisorCard, type Advisor } from "@/features/advisors"

import GeneralBg from "@/assets/img/profile/advisorQuiz/general_background.webp"

export interface MatchedAdvisor {
  badge: string
  advisor: Advisor
}

interface QuizResultProps {
  advisors: MatchedAdvisor[]
  onClose: () => void
  onStartChat: (advisor: Advisor) => void
}

const CARD_STEP = 288

/** Matched-advisor carousel. Ported from AdvisorsQuiz Step8 Result. */
export function QuizResult({ advisors, onClose, onStartChat }: QuizResultProps) {
  const [active, setActive] = useState(0)

  return (
    <div className="relative min-h-[100dvh] bg-dark-base">
      <img
        src={GeneralBg}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full object-cover"
      />

      <div className="relative z-[1] mx-auto w-full max-w-[500px]">
        <div className="relative flex h-14 items-center justify-end px-4">
          <button type="button" onClick={onClose} aria-label="Close" className="text-purple-light">
            <X className="size-6" />
          </button>
        </div>

        <div className="flex flex-col items-center px-4">
          <div className="mb-4 flex max-w-[500px] flex-col gap-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Tailored Just for You
            </h2>
            <p className="font-body text-base text-purple-light/70">
              After carefully analyzing your responses, we&apos;ve selected the top advisors whose
              guidance and energy match your needs the closest
            </p>
          </div>

          <div className="mb-6 flex items-center justify-center gap-2">
            {advisors.map((item, index) => (
              <span
                key={item.advisor.assistantId}
                className={`size-2 rounded-full transition-colors ${index === active ? "bg-main-purple" : "bg-purple-light/20"}`}
              />
            ))}
          </div>

          <div
            onScroll={(e) =>
              setActive(
                Math.max(
                  0,
                  Math.min(advisors.length - 1, Math.round(e.currentTarget.scrollLeft / CARD_STEP)),
                ),
              )
            }
            className="flex w-full snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-6 [scrollbar-width:none]"
            style={{ paddingInline: "max(16px, calc((100% - 272px) / 2))" }}
          >
            {advisors.map((item, index) => (
              <div
                key={`${item.advisor.assistantId}-${item.badge}`}
                className={`w-[272px] min-w-[272px] snap-center rounded-[32px] border border-purple-light/20 bg-dark-base/70 px-4 pb-4 pt-3 transition-opacity ${
                  index === active ? "opacity-100" : "opacity-30"
                }`}
              >
                <p className="mb-3 text-center font-display text-base font-medium text-purple-light">
                  {item.badge}
                </p>
                <AdvisorCard
                  advisor={item.advisor}
                  onButtonClick={() => onStartChat(item.advisor)}
                  buttonTitle="Start chat"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
